import React, { useState, useEffect, useCallback } from 'react';
import { Outlet } from 'react-router-dom';
import NavBar from './components/NavBar/NavBar';
import useApi from './hooks/useApi';
import { Application, Role, Permission } from './types';

const AuthenticatedLayout: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const { loading, error, securedRequest } = useApi();

  const fetchApplications = useCallback(async (signal?: AbortSignal) => {
    const { data, error } = await securedRequest('get', '/api/applications', null, signal);
    if (data) {
      const sortedData = [...data].sort((a: Application, b: Application) =>
        a.name.localeCompare(b.name)
      );
      setApplications(sortedData);
    }
    if (error && !signal?.aborted) {
      console.error('Failed to fetch applications:', error);
    }
  }, [securedRequest]);

  useEffect(() => {
    const controller = new AbortController();
    const fetchInitialData = async () => {
      const { data: userData, error: userError } = await securedRequest('get', '/api/users', null, controller.signal);
      if (userData) {
        setIsAdmin(userData.isAdmin || false);
      }
      if (userError && !controller.signal.aborted) {
        console.error('Failed to fetch user data:', userError);
      }
      fetchApplications(controller.signal);
    };

    fetchInitialData();
    return () => controller.abort();
  }, [securedRequest, fetchApplications]);

  const handleAppAdded = (newApp: Application) => {
    setApplications(prevApps =>
      [...prevApps, newApp].sort((a, b) => a.name.localeCompare(b.name))
    );
  };

  const handleAppRemoved = async (appId: string | number) => {
    const { error } = await securedRequest('delete', `/api/applications/${appId}`);
    if (!error) {
      setApplications(prevApps => prevApps.filter(app => app.id !== appId));
      return true;
    } else {
      console.error(`Failed to delete app ${appId}:`, error);
      alert('Could not remove application.');
      return false;
    }
  };

  const handleAppUpdated = (updatedApp: Application) => {
    setApplications(prevApps =>
      prevApps.map(app => (app.id === updatedApp.id ? updatedApp : app))
        .sort((a, b) => a.name.localeCompare(b.name))
    );
  };

  return (
    <>
      <NavBar isAdmin={isAdmin} />
      <Outlet context={{
        applications, isAdmin, isLoading: loading, error,
        handleAppAdded, handleAppRemoved, handleAppUpdated
      }} />
    </>
  );
};

export default AuthenticatedLayout;
